<template>
  <li v-if="item" :class="{selected: isSelected}">
    <div
      :style="treeItemDivStyles"
      :class="{bold: isFolder, selected: isSelected}"
      class="element-div"
      :data-uuid="item.uuid"
      @click="toggle">
      <font-awesome-icon v-if="isComponent" icon="folder" class="plus-icon"/>
      <font-awesome-icon v-else icon="file" class="plus-icon"/>
      {{ itemName }} {{ isHidden ? '(Hidden)' : '' }}
      <span v-if="isFolder">[{{ isOpen ? '-' : '+' }}]</span>
      <br/>
      <small>({{item.properties.element_type}}) - {{item.order}}</small>
    </div>
    <ul v-if="isFolder && shouldBeOpen">

      <template v-if="authorize(permissibleActions.reorderable.minPermissionLevel)">
        <draggable v-model="childrenShadow" :disabled="lockInteraction" @end="dragEnd(childrenShadow)">
          <PageEditorTreeItem
              class="item"
              :focus="focus"
              :reload="reload"
              :userInteraction="userInteraction"
              :lockInteraction="lockInteraction"
              v-for="child in item.children"
              :key="child.uuid"
              :item="child"
              :elementPath="elementPath"
              :data-uuid="child.uuid"
              @onChange="onChange"
          ></PageEditorTreeItem>
        </draggable>

        <draggable v-model="parentsShadow" :disabled="lockInteraction" @end="dragEnd(parentsShadow)">
          <PageEditorTreeItem
              class="item"
              :focus="focus"
              :reload="reload"
              :userInteraction="userInteraction"
              :lockInteraction="lockInteraction"
              @onChange="onChange"
              v-for="child in item.parent_elements"
              :key="child.uuid"
              :item="child"
              :elementPath="elementPath"
              :data-uuid="child.uuid"
          ></PageEditorTreeItem>
        </draggable>
      </template>

      <template v-else>
        <PageEditorTreeItem
            class="item"
            :focus="focus"
            :reload="reload"
            :userInteraction="userInteraction"
            :lockInteraction="lockInteraction"
            v-for="child in item.children"
            :key="child.uuid"
            :item="child"
            :elementPath="elementPath"
            :data-uuid="child.uuid"
            @onChange="onChange"
        ></PageEditorTreeItem>

        <PageEditorTreeItem
            class="item"
            :focus="focus"
            :reload="reload"
            :userInteraction="userInteraction"
            :lockInteraction="lockInteraction"
            @onChange="onChange"
            v-for="child in item.parent_elements"
            :key="child.uuid"
            :item="child"
            :elementPath="elementPath"
            :data-uuid="child.uuid"
        ></PageEditorTreeItem>
      </template>

      <template v-if="authorize(permissibleActions.createable.minPermissionLevel)">
        <li :style="addBtnStyles" class="add" v-if="canAdd" @click="addItem(item)">+</li>
      </template>
    </ul>
  </li>
</template>

<script>
import draggable from 'vuedraggable';
import { DataPropertiesService } from '@/common/services/data_properties.servicejs';
import EditorEvents from '../../mixins/editor-events';
import PropertyOptions from '../../mixins/property-options';
import { usePageEditorStore } from '@/stores/PageEditorStore';
import PageEditorPermissionSubsystem from '../../mixins/page-editor-permissions-subsystem';

export default {
  name: 'PageEditorTreeItem',
  mixins: [EditorEvents, PropertyOptions, PageEditorPermissionSubsystem],
  components: {
    draggable,
  },
  props: {
    focus: Object,
    userInteraction: Object,
    item: Object,
    reload: Boolean,
    lockInteraction: Boolean,
    elementPath: Array,
  },
  setup() {
    const pageEditorStore = usePageEditorStore();
    return {
      pageEditorStore,
    };
  },
  data() {
    return {
      isOpen: false,
      hidden: false,
      childrenShadow: [],
      parentsShadow: [],
      kioskId: null,
      optionsSheet: {},
      minPermissionLevel: null,
      indentationLevel: this.item.path.length - 1
    };
  },
  created() {
    this.kioskId = this.$route.params.kioskId;
    this.createShadows();
    this.optionsSheet = this.loadPropertyOptions(this.item);
    this.permissionLevel = this.pageEditorStore.getPermissionLevel;
  },
  watch: {
    /**
     * Reload the page, pretty clear
     */
    reload() {
      this.createShadows();
    },
  },
  computed: {
    /**
     * Detect if the item is a folder
     * @returns {boolean}
     */
    isFolder() {
      let result = false;
      if (this.item.children && this.item.children.length > 0) {
        result = true;
      }
      if (this.item.parent_elements && this.item.parent_elements.length > 0) {
        result = true;
      }
      return result;
    },
    /**
     * Get the items name OR just call it an unnamed object model name
     */
    itemName() {
      let { name } = this.item;
      if (this.item && this.item.name === null) {
        name = `Unnamed ${this.item.object_model_name}`;
      }
      return name;
    },
    /**
     * Determine if it's selected by a mouse click or in the element path
     * @returns {boolean}
     */
    isSelected() {
      let result = false;
      if (this.focus) {
        if (this.item.uuid === this.focus.uuid) {
          result = true;
        }
      }
      return result;
    },
    /**
     * Determine if this is a component or an element
     * @returns {boolean}
     */
    isComponent() {
      let result = false;
      if (this.item) {
        if (this.item.object_model_name === 'Component') {
          result = true;
        }
      }
      return result;
    },
    /**
     * Determine if the item is hidden or not
     * @returns {boolean}
     */
    isHidden() {
      let result = false;
      if (this.item) {
        if (this.item.properties) {
          if (this.item.properties.hidden === true) {
            result = true;
          }
        }
      }
      return result;
    },
    /**
     * Determine if the item properties allow for nested components
     * @returns {boolean}
     */
    canAdd() {
      let result = true;
      if (this.item) {
        if (this.optionsSheet) {
          if (this.optionsSheet.options.allow_nested_components === false) {
            result = false;
          }
        }
      }
      return result;
    },
    /**
     * Determine if the item should be opened or closed (expanded or closed)
     * @returns {boolean}
     */
    shouldBeOpen() {
      let result = this.isOpen;
      if (result === false && this.elementPath) {
        for (let i = 0; i < this.elementPath.length; i += 1) {
          if (this.elementPath[i] === this.item.uuid) {
            if (i !== (this.elementPath.length - 1)) {
              result = true;
            }
          }
        }
      }
      return result;
    },
    treeItemDivStyles() {
      return {
        padding: `10px 15px 10px ${(15 * this.indentationLevel) + 15}px`
      }
    },
    addBtnStyles() {
      return {
        padding: `8px 15px 10px ${(15 * (this.indentationLevel + 1)) + 15}px`
      }
    }
  },
  methods: {
    /**
     * Toggle open
     */
    toggle() {
      this.setFocus();
      if (this.isFolder) {
        this.isOpen = !this.isOpen;
        this.hidden = this.isHidden;
      }
    },
    /**
     * Filter up changes
     * @param object
     */
    onChange(object) {
      this.$emit('onChange', object);
    },
    /**
     * Send a focus signal
     */
    setFocus() {
      this.$emit('onChange', this.item);
    },
    /**
     * Create a shadow list of elements
     */
    createShadows() {
      this.childrenShadow = this.item.children;
      this.parentsShadow = this.item.parent_elements;
    },
    /**
     * When the drag end is done, compile the new order of shadows and send that off to update
     * the dataObjects and reorder the non shadow copy
     * @param shadows
     */
    dragEnd(shadows) {
      const updateBuffer = [];
      for (let i = 0; i < shadows.length; i += 1) {
        const shadow = shadows[i];
        if (shadow) {
          const obj = {
            component: shadow,
            model_order: i + 1,
            name: shadow.name,
            id: shadow.id,
          };

          updateBuffer.push(obj);
        }
      }
      this.log('dragEnd', 'updateBuffer', updateBuffer);
      for (let i = 0; i < updateBuffer.length; i += 1) {
        const updateTask = updateBuffer[i];
        const { component } = updateTask;
        if (component) {
          component.properties.model_order = updateTask.model_order;
          DataPropertiesService.update(component, this.kioskId)
            .then(data => DataPropertiesService.show(data, this.kioskId))
            .then(data => this.updateObject(data));
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .element-div {
    position: relative;
    padding: 0 15px;
  }

  .element-div::before {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0;
    width: 100%;
    height: 2px;
    background-color: #fc2f70;
    transform-origin: center;
    transform: translate(-50%, 0) scaleX(0);
    transition: transform 0.3s ease-in-out;
  }

  .element-div:hover::before {
    transform: translate(-50%, 0) scaleX(1);
  }

  .bold {
    font-weight: bold;
  }
  ul {
    line-height: 1.5em;
  }
  .item {
    cursor: pointer;
    text-align: left;
  }
  .folder {
    text-decoration-style: solid;
    margin-bottom: 10px;
  }
  .selected {
    background-color: #e3e3e3;
    &.element-div {
      padding-top: 20px !important;
    }
    + ul .add {
      padding-bottom: 16px !important;
    }
  }
</style>
